import React, { useState, useEffect } from "react";
import { Card, Empty, Pagination, Skeleton } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { isEmpty } from "lodash";
import { formatNumber } from "../../utils/formatters";
import CustomTypography from "../commonComponents/CustomTypography";
import DebounceInput from "../commonComponents/DebounceInput";
import useOttoAnalyticsQueries from "../../hooks/useOttoAnalyticsQueries";
import { COMMON_PAGINATION } from "../../library/constants";
import { VEHICLE_TRADE_CATEGORIES } from "../../constant/OttoAnalyticsConstants";

const VehicleTradeDrawer = ({ tradeType, labelKey, labelSuffix, queryVariables }) => {
  const { fetchVehicleTradeOverview, vehicleTradeOverviewQuery } = useOttoAnalyticsQueries();

  const [searchValue, setSearchValue] = useState("");
  const [pagination, setPagination] = useState({
    current: COMMON_PAGINATION.DEFAULT_CURRENT_PAGE,
    pageSize: COMMON_PAGINATION.DEFAULT_PAGE_SIZE,
  });

  // Fetch data whenever search or pagination changes
  useEffect(() => {
    const variables = {
      ...queryVariables,
      category: tradeType,
      search: searchValue,
      page: pagination.current,
      pageSize: pagination.pageSize,
    };

    fetchVehicleTradeOverview({ variables });
  }, [searchValue, pagination, queryVariables, tradeType, fetchVehicleTradeOverview]);

  const handleSearchChange = value => {
    setSearchValue(value);
    // Reset to first page on search
    setPagination(prev => ({
      ...prev,
      current: COMMON_PAGINATION.DEFAULT_CURRENT_PAGE,
    }));
  };

  const handlePaginationChange = (page, pageSize) => {
    setPagination(prev => ({
      current: prev.pageSize !== pageSize ? COMMON_PAGINATION.DEFAULT_CURRENT_PAGE : page,
      pageSize,
    }));
  };

  const isLoading = vehicleTradeOverviewQuery.loading;
  const data = vehicleTradeOverviewQuery.data?.getVehicleTradeOverview;

  // Get vehicles and total count based on trade type
  const vehicles =
    tradeType === VEHICLE_TRADE_CATEGORIES.BUY
      ? data?.buyData || []
      : tradeType === VEHICLE_TRADE_CATEGORIES.SELL
      ? data?.sellData || []
      : data?.soldData || [];

  const totalCount =
    tradeType === VEHICLE_TRADE_CATEGORIES.BUY
      ? data?.buyTotalCount || 0
      : tradeType === VEHICLE_TRADE_CATEGORIES.SELL
      ? data?.sellTotalCount || 0
      : data?.soldTotalCount || 0;

  return (
    <div className="flex flex-col h-full">
      {/* Sticky Search Bar at Top */}
      <div className="sticky top-0 bg-white z-10 py-4">
        <DebounceInput
          placeholder="Search vehicle"
          value={searchValue}
          onChange={handleSearchChange}
          suffix={<SearchOutlined className="icon-color" />}
          allowClear
        />
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto min-h-[calc(100vh-226px)]">
        {isLoading ? (
          <div className="grid grid-cols-1 gap-3">
            {[...Array(6)].map((_, index) => (
              <Card key={index} styles={{ body: { padding: "12px" } }}>
                <Skeleton active paragraph={{ rows: 1 }} />
              </Card>
            ))}
          </div>
        ) : !isEmpty(vehicles) ? (
          <div className="grid grid-cols-1 gap-3">
            {vehicles.map((vehicle, index) => (
              <Card key={index} styles={{ body: { padding: "12px" } }}>
                <CustomTypography
                  variant="text"
                  strong
                  style={{ fontSize: "16px" }}
                  className="block mb-1"
                >
                  {vehicle.vehicleName}
                </CustomTypography>
                <CustomTypography
                  variant="text"
                  style={{ fontSize: "14px" }}
                  className="text-[#000000A6]"
                  type="secondary"
                >
                  {formatNumber(vehicle[labelKey])} {labelSuffix}
                </CustomTypography>
              </Card>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center min-h-[calc(100vh - 252px)]">
            <Empty description={searchValue ? "No vehicles found" : "No data available"} />
          </div>
        )}
      </div>

      {/* Sticky Pagination at Bottom */}
      {!isEmpty(vehicles) && !isLoading && (
        <div className="sticky bottom-0 bg-white z-10 py-4 flex justify-center border-t border-gray-200">
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={totalCount}
            onChange={handlePaginationChange}
            size="small"
            showSizeChanger
            pageSizeOptions={COMMON_PAGINATION.PAGE_SIZE_OPTIONS}
            // showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
          />
        </div>
      )}
    </div>
  );
};

export default VehicleTradeDrawer;
